import { useCallback, useMemo, useRef, useState, type DragEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, ChevronLeft, ChevronRight, ImagePlus, RotateCw, Trash2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '../../lib/api/api-context.js';
import type { ImageCodec } from '../../lib/media/image-prep.js';
import { useShopWatermark } from '../../lib/media/use-shop-watermark.js';
import {
  deleteMedia,
  listMedia,
  reorderMedia,
  type putToStorage,
} from '../../lib/media/media-api.js';
import type { Media } from '../../lib/media/media-contract.js';
import { useMediaUploads, type UploadItem } from '../../lib/media/use-media-uploads.js';
import { cn, Button, useConfirm, useToast } from '@tailoring-crm/ui';
import { MediaLightbox } from './media-lightbox.js';

/**
 * The reusable photo uploader.
 *
 * Orders, order items, measurement profiles and expenses all attach photographs, and all of them
 * mount THIS component rather than building their own — they differ only in the `entityType` and
 * `entityId` they pass. That is what makes media a pipeline rather than a feature of orders.
 *
 * Everything here is shaped by one user: a shop owner holding a phone in one hand and a tape
 * measure in the other, on a connection that comes and goes.
 *
 *  - "Take photo" opens the camera directly, because that is the common case, and it sits first.
 *  - Compression happens before any transfer, so the progress bar means something.
 *  - Every file has its own progress, its own failure and its own retry. One photo failing out of
 *    six does not lose the other five, and retrying does not mean photographing anything again.
 *  - Reordering is buttons, not drag-and-drop: dragging a thumbnail on a phone fights with
 *    scrolling, and loses. Desktop keeps drag-and-drop for DROPPING FILES IN, where it is good.
 */

export interface MediaUploaderProps {
  /** Opaque parent type — 'order', 'measurement_profile', 'expense', … */
  entityType: string;
  /** Null while the parent is unsaved; attach the media once it has an id. */
  entityId?: string | null;
  kind?: string;
  visibility?: 'internal' | 'customer_visible';
  /** Files an upload into a gallery folder on arrival. Optional; every other caller leaves it unset. */
  folderId?: string | null;
  maxFiles?: number;
  disabled?: boolean;
  className?: string;
  /** Fires whenever the confirmed set of images changes. */
  onChange?: (media: Media[]) => void;
  /** Test seams: a stub codec and a stub uploader, so no canvas or network is needed. */
  codec?: ImageCodec;
  put?: typeof putToStorage;
}

export function MediaUploader({
  entityType,
  entityId = null,
  kind,
  visibility,
  folderId,
  maxFiles = 10,
  disabled = false,
  className,
  onChange,
  codec,
  put,
}: MediaUploaderProps): ReactElement {
  const { t } = useTranslation();
  const client = useApiClient();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { toast } = useToast();

  const [dragging, setDragging] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const galleryInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);

  const listKey = useMemo(
    () => ['media', entityType, entityId, kind ?? null] as const,
    [entityType, entityId, kind],
  );

  const existing = useQuery({
    queryKey: listKey,
    queryFn: async () => listMedia(client, { entityType, entityId, ...(kind ? { kind } : {}) }),
    // Nothing to list until the parent exists.
    enabled: entityId !== null,
  });

  const media = useMemo(() => existing.data ?? [], [existing.data]);

  const refresh = useCallback(async () => {
    const next = await queryClient.fetchQuery({
      queryKey: listKey,
      queryFn: async () => listMedia(client, { entityType, entityId, ...(kind ? { kind } : {}) }),
    });
    onChange?.(next);
  }, [client, entityId, entityType, kind, listKey, onChange, queryClient]);

  /**
   * The shop's watermark, when it has one switched on and this uploader is one whose images a
   * customer sees. `customer_visible` is the test rather than a prop of its own: an expense
   * receipt and a measurement sheet are internal, and stamping the shop's name across those
   * would be branding a filing cabinet.
   */
  const watermark = useShopWatermark(visibility === 'customer_visible');

  const uploads = useMediaUploads({
    entityType,
    entityId,
    ...(watermark ? { watermark } : {}),
    ...(kind ? { kind } : {}),
    ...(visibility ? { visibility } : {}),
    ...(folderId !== undefined ? { folderId } : {}),
    ...(codec ? { codec } : {}),
    ...(put ? { put } : {}),
    onUploaded: (uploaded) => {
      if (entityId === null) {
        // No parent yet, so there is no list to refetch — surface it optimistically instead.
        queryClient.setQueryData<Media[]>(listKey, (current) => [...(current ?? []), uploaded]);
        onChange?.([...(queryClient.getQueryData<Media[]>(listKey) ?? [])]);
        return;
      }
      void refresh();
    },
  });

  const remove = useMutation({
    mutationFn: async (mediaId: string) => deleteMedia(client, mediaId),
    onSuccess: async () => {
      toast({ title: t('media.toast.deleted') });
      await refresh();
    },
    onError: () => toast({ title: t('media.error.deleteFailed'), variant: 'destructive' }),
  });

  const reorder = useMutation({
    mutationFn: async (order: string[]) => reorderMedia(client, { entityType, entityId, order }),
    onSuccess: (items) => {
      queryClient.setQueryData(listKey, items);
      onChange?.(items);
    },
    onError: () => toast({ title: t('media.error.reorderFailed'), variant: 'destructive' }),
  });

  const inFlight = uploads.items.filter((item) => item.status !== 'done');
  const total = media.length + inFlight.length;

  const accept = useCallback(
    (files: FileList | null) => {
      if (!files || files.length === 0) return;
      const chosen = Array.from(files).filter((file) => file.type.startsWith('image/'));
      const room = maxFiles - total;
      if (room <= 0) {
        toast({ title: t('media.error.tooMany', { count: maxFiles }), variant: 'destructive' });
        return;
      }
      if (chosen.length > room) {
        toast({ title: t('media.error.tooMany', { count: maxFiles }), variant: 'destructive' });
      }
      void uploads.addFiles(chosen.slice(0, room));
    },
    [maxFiles, t, toast, total, uploads],
  );

  const onDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDragging(false);
    if (!disabled) accept(event.dataTransfer.files);
  };

  const move = (mediaId: string, delta: number): void => {
    const order = media.map((item) => item.id);
    const from = order.indexOf(mediaId);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= order.length) return;
    const next = [...order];
    const [moved] = next.splice(from, 1);
    if (moved) next.splice(to, 0, moved);
    reorder.mutate(next);
  };

  const askToDelete = async (item: Media): Promise<void> => {
    const confirmed = await confirm({
      title: t('media.delete.title'),
      description: t('media.delete.description'),
      confirmLabel: t('media.delete.confirm'),
      destructive: true,
    });
    if (confirmed) remove.mutate(item.id);
  };

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {/* Drag-and-drop is a desktop affordance; the buttons inside it are the mobile one. */}
      <div
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={cn(
          'flex flex-col items-center gap-3 rounded-lg border-2 border-dashed border-border p-4 text-center transition-colors',
          dragging && 'border-primary bg-accent',
          disabled && 'pointer-events-none opacity-50',
        )}
      >
        <p className="text-sm text-muted-foreground">{t('media.dropzone.hint')}</p>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <Button onClick={() => cameraInput.current?.click()} disabled={disabled}>
            <Camera aria-hidden="true" />
            {t('media.action.camera')}
          </Button>
          <Button
            variant="outline"
            onClick={() => galleryInput.current?.click()}
            disabled={disabled}
          >
            <ImagePlus aria-hidden="true" />
            {t('media.action.choose')}
          </Button>
        </div>

        <p className="text-xs text-muted-foreground">
          {t('media.dropzone.limit', { count: maxFiles })}
        </p>

        <input
          ref={cameraInput}
          type="file"
          accept="image/*"
          // Opens the rear camera straight away on a phone, which is where a garment is.
          capture="environment"
          className="sr-only"
          onChange={(event) => {
            accept(event.target.files);
            event.target.value = '';
          }}
          aria-label={t('media.action.camera')}
        />
        <input
          ref={galleryInput}
          type="file"
          accept="image/*"
          multiple
          className="sr-only"
          onChange={(event) => {
            accept(event.target.files);
            event.target.value = '';
          }}
          aria-label={t('media.action.choose')}
        />
      </div>

      {(media.length > 0 || inFlight.length > 0) && (
        <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="media-grid">
          {media.map((item, index) => (
            <li
              key={item.id}
              className="group relative overflow-hidden rounded-md border border-border"
            >
              <button
                type="button"
                onClick={() => setActiveId(item.id)}
                className="block aspect-square w-full"
                aria-label={t('media.action.view', {
                  name: item.caption ?? item.originalFilename,
                })}
              >
                {/* The thumbnail, never the full image. */}
                {item.thumbnailUrl ? (
                  <img
                    src={item.thumbnailUrl}
                    alt={item.caption ?? item.originalFilename}
                    loading="lazy"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <span className="flex h-full w-full items-center justify-center bg-muted" />
                )}
              </button>

              <div className="absolute inset-x-0 bottom-0 flex justify-between bg-background/85 p-1">
                <span className="flex gap-0.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={index === 0 || reorder.isPending}
                    onClick={() => move(item.id, -1)}
                    aria-label={t('media.action.moveEarlier')}
                  >
                    <ChevronLeft aria-hidden="true" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    disabled={index === media.length - 1 || reorder.isPending}
                    onClick={() => move(item.id, 1)}
                    aria-label={t('media.action.moveLater')}
                  >
                    <ChevronRight aria-hidden="true" />
                  </Button>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-destructive"
                  onClick={() => void askToDelete(item)}
                  aria-label={t('media.action.delete')}
                >
                  <Trash2 aria-hidden="true" />
                </Button>
              </div>
            </li>
          ))}

          {inFlight.map((item) => (
            <UploadCard
              key={item.clientId}
              item={item}
              onRetry={() => void uploads.retry(item.clientId)}
              onDismiss={() => uploads.dismiss(item.clientId)}
            />
          ))}
        </ul>
      )}

      <MediaLightbox items={media} activeId={activeId} onActiveIdChange={setActiveId} />
    </div>
  );
}

/** One file on its way up: its own preview, its own progress, its own failure, its own retry. */
function UploadCard({
  item,
  onRetry,
  onDismiss,
}: {
  item: UploadItem;
  onRetry: () => void;
  onDismiss: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const failed = item.status === 'failed';
  const percent = Math.round(item.progress * 100);

  return (
    <li
      className="relative overflow-hidden rounded-md border border-border"
      data-testid="upload-item"
      data-status={item.status}
    >
      <div className="aspect-square w-full bg-muted">
        {item.previewUrl && (
          <img src={item.previewUrl} alt="" className="h-full w-full object-cover opacity-70" />
        )}
      </div>

      <div className="absolute inset-x-0 bottom-0 bg-background/90 p-1.5">
        {failed ? (
          <div className="flex items-center justify-between gap-1">
            <span className="truncate text-xs text-destructive">
              {t(item.errorKey ?? 'media.error.uploadFailed')}
            </span>
            <span className="flex shrink-0">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={onRetry}
                aria-label={t('media.action.retry')}
              >
                <RotateCw aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={onDismiss}
                aria-label={t('media.action.discard')}
              >
                <X aria-hidden="true" />
              </Button>
            </span>
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <span className="truncate text-[11px] text-muted-foreground">
              {t(`media.status.${item.status}`)}
            </span>
            <div
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={t('media.progress', { name: item.fileName })}
              className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
            >
              <div
                className="h-full bg-primary transition-[width]"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </li>
  );
}
